import { type Component, createSignal, For, Show, onMount, onCleanup } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix default marker icons stripped by bundler
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

export interface ParsedAddress {
  readonly venueName: string
  readonly addressLine1: string
  readonly city: string
  readonly state: string
  readonly zipCode: string
  readonly country: string
  readonly countryCode: string
  readonly lat: number
  readonly lng: number
}

interface SearchResult {
  readonly lat: number
  readonly lng: number
  readonly displayName: string
  readonly parsed: ParsedAddress
}

interface LocationMapProps {
  readonly onLocationPicked?: (parsed: ParsedAddress) => void
}

const searchVenues = async (query: string): Promise<SearchResult[]> => {
  if (!query || query.trim().length < 3) return []
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&q=${encodeURIComponent(query)}`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'chorus-admin' } }
    )
    const data = await res.json()
    return data.map((item: Record<string, unknown>) => {
      const a = (item.address ?? {}) as Record<string, string>
      const lt = parseFloat(item.lat as string)
      const ln = parseFloat(item.lon as string)
      return {
        lat: lt,
        lng: ln,
        displayName: item.display_name as string,
        parsed: {
          venueName: (item.name as string) || a.leisure || a.amenity || a.building || '',
          addressLine1: [a.road, a.house_number].filter(Boolean).join(' '),
          city: a.city || a.town || a.village || a.municipality || '',
          state: a.state || '',
          zipCode: a.postcode || '',
          country: a.country || '',
          countryCode: (a.country_code || '').toUpperCase(),
          lat: lt,
          lng: ln
        }
      }
    })
  } catch {
    return []
  }
}

const reverseGeocode = async (lat: number, lng: number): Promise<ParsedAddress | null> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'chorus-admin' } }
    )
    const data = await res.json()
    const a = data.address ?? {}
    return {
      venueName: data.name || a.leisure || a.amenity || a.building || '',
      addressLine1: [a.road, a.house_number].filter(Boolean).join(' '),
      city: a.city || a.town || a.village || a.municipality || '',
      state: a.state || '',
      zipCode: a.postcode || '',
      country: a.country || '',
      countryCode: (a.country_code || '').toUpperCase(),
      lat,
      lng
    }
  } catch {
    return null
  }
}

const LocationMap: Component<LocationMapProps> = (props) => {
  let containerRef: HTMLDivElement | undefined
  let map: L.Map | undefined
  let marker: L.Marker | undefined
  let debounceTimer: ReturnType<typeof setTimeout> | undefined

  const [query, setQuery] = createSignal('')
  const [results, setResults] = createSignal<SearchResult[]>([])
  const [isSearching, setIsSearching] = createSignal(false)
  const [showResults, setShowResults] = createSignal(false)

  const handleSearch = (value: string): void => {
    setQuery(value)
    if (debounceTimer) clearTimeout(debounceTimer)
    if (value.trim().length < 3) {
      setResults([])
      setShowResults(false)
      return
    }
    setIsSearching(true)
    debounceTimer = setTimeout(async () => {
      const res = await searchVenues(value)
      setResults(res)
      setShowResults(res.length > 0)
      setIsSearching(false)
    }, 400)
  }

  const selectResult = (result: SearchResult): void => {
    setQuery(result.parsed.venueName || result.displayName.split(',')[0])
    setShowResults(false)
    setResults([])

    if (map && marker) {
      marker.setLatLng([result.lat, result.lng])
      map.flyTo([result.lat, result.lng], 16, { duration: 0.8 })
    }

    props.onLocationPicked?.(result.parsed)
  }

  onMount(() => {
    if (!containerRef) return

    map = L.map(containerRef, {
      center: [39.8283, -98.5795],
      zoom: 4,
      zoomControl: false
    })

    L.control.zoom({ position: 'bottomright' }).addTo(map)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OSM'
    }).addTo(map)

    marker = L.marker([39.8283, -98.5795], { draggable: true }).addTo(map)

    marker.on('dragend', async () => {
      const pos = marker!.getLatLng()
      const parsed = await reverseGeocode(pos.lat, pos.lng)
      if (parsed) {
        setQuery(parsed.venueName || '')
        props.onLocationPicked?.(parsed)
      }
    })
  })

  onCleanup(() => {
    if (debounceTimer) clearTimeout(debounceTimer)
    map?.remove()
  })

  return (
    <div class="relative w-full h-full">
      {/* Map */}
      <div ref={containerRef} class="w-full h-full rounded-lg overflow-hidden" />

      {/* Search overlay */}
      <div class="absolute top-3 left-3 right-3 z-[1000]">
        <div class="relative">
          <div class="flex items-center gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
            <Icon icon="heroicons:magnifying-glass" class="text-[1rem] text-gray-400 shrink-0" />
            <input
              type="text"
              value={query()}
              onInput={(e) => handleSearch(e.currentTarget.value)}
              onFocus={() => {
                if (results().length > 0) setShowResults(true)
              }}
              placeholder="Search venue or address..."
              class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
            />
            <Show when={isSearching()}>
              <div class="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
            </Show>
            <Show when={query() && !isSearching()}>
              <button
                type="button"
                class="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                onClick={() => {
                  setQuery('')
                  setResults([])
                  setShowResults(false)
                }}
              >
                <Icon icon="heroicons:x-mark" class="text-[1rem]" />
              </button>
            </Show>
          </div>

          {/* Results dropdown */}
          <Show when={showResults()}>
            <div class="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden max-h-48 overflow-y-auto animate-menu-enter">
              <For each={results()}>
                {(result) => (
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors duration-100 border-b border-gray-100 last:border-b-0 flex items-start gap-2"
                    onClick={() => selectResult(result)}
                  >
                    <Icon icon="heroicons:map-pin" class="text-[1rem] text-gray-400 shrink-0 mt-0.5" />
                    <div class="flex-1 min-w-0">
                      <Show when={result.parsed.venueName}>
                        <span class="font-medium text-gray-900 block truncate">{result.parsed.venueName}</span>
                      </Show>
                      <span class="text-gray-500 text-xs block truncate">{result.displayName}</span>
                    </div>
                  </button>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default LocationMap
